// NotificationPreferencesGrid — 5-row × 2-channel matrix.
//
// Categories: booking, receipt, invoice, cancellation, bacs.
// Channels: email (with email_purpose dropdown), sms.
//
// Saves the full preferences array to the bulk PUT endpoint. The dropdown
// for "Send from:" only lists email purposes that are configured AND active.

import { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { useQueries } from '@tanstack/react-query';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../hooks/useCommunicationSettings';
import { getEmailSettings, getSmsSettings } from '../../../../api/communication';
import { queryKeys } from '../../../../api/queryKeys';
import type {
  NotificationCategory,
  NotificationChannel,
  NotificationPreference,
  EmailSettings,
  SmsSettings,
} from '../../../../types/api';
import styles from './NotificationPreferencesGrid.module.css';

const CATEGORIES: ReadonlyArray<{
  id: NotificationCategory;
  label: string;
  description: string;
  defaultPurpose: 'notifications' | 'receipts' | 'invoices';
}> = [
  {
    id: 'booking',
    label: 'Bookings',
    description:
      'Confirmations and reminders when a booking is created or updated.',
    defaultPurpose: 'notifications',
  },
  {
    id: 'receipt',
    label: 'Receipts',
    description:
      'PDF receipt delivered to the customer after a collection is completed.',
    defaultPurpose: 'receipts',
  },
  {
    id: 'invoice',
    label: 'Invoices',
    description: 'Invoice issued to the customer with payment details.',
    defaultPurpose: 'invoices',
  },
  {
    id: 'cancellation',
    label: 'Cancellations',
    description:
      'Notice sent when a booking is cancelled by the customer or your team.',
    defaultPurpose: 'notifications',
  },
  {
    id: 'bacs',
    label: 'BACS',
    description: 'Payment-run statements sent to suppliers / vendors.',
    defaultPurpose: 'invoices',
  },
];

const OUTBOUND_PURPOSES = ['notifications', 'receipts', 'invoices'] as const;
type OutboundPurpose = (typeof OUTBOUND_PURPOSES)[number];

const PURPOSE_LABEL: Record<OutboundPurpose, string> = {
  notifications: 'Notifications',
  receipts: 'Receipts',
  invoices: 'Invoices',
};

function findPref(
  prefs: NotificationPreference[],
  category: NotificationCategory,
  channel: NotificationChannel,
): NotificationPreference | null {
  return (
    prefs.find((p) => p.category === category && p.channel === channel) ?? null
  );
}

export function NotificationPreferencesGrid(): JSX.Element {
  const prefsQuery = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  // Load each outbound email purpose to know which are active.
  const emailQueries = useQueries({
    queries: OUTBOUND_PURPOSES.map((purpose) => ({
      queryKey: queryKeys.communication.email(purpose),
      queryFn: () => getEmailSettings(purpose),
      staleTime: 30_000,
      retry: false,
    })),
  });

  const smsQuery = useQueries({
    queries: [
      {
        queryKey: queryKeys.communication.sms(),
        queryFn: () => getSmsSettings(),
        staleTime: 30_000,
        retry: false,
      },
    ],
  })[0];

  const activeEmailPurposes = useMemo<Set<OutboundPurpose>>(() => {
    const set = new Set<OutboundPurpose>();
    OUTBOUND_PURPOSES.forEach((purpose, idx) => {
      const q = emailQueries[idx];
      const data = q?.data as EmailSettings | undefined;
      if (data && data.is_active && data.smtp_host) set.add(purpose);
    });
    return set;
  }, [emailQueries]);

  const smsConfigured = useMemo(() => {
    const data = smsQuery?.data as SmsSettings | null | undefined;
    return data !== null && data !== undefined && data.is_active;
  }, [smsQuery?.data]);

  const [draft, setDraft] = useState<NotificationPreference[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState<NotificationPreference[]>(
    [],
  );
  const [savedFlash, setSavedFlash] = useState(false);

  // Hydrate when the server response arrives or after a successful save.
  useEffect(() => {
    if (!prefsQuery.data) return;
    const next: NotificationPreference[] = [];
    CATEGORIES.forEach((cat) => {
      const email = findPref(prefsQuery.data!.preferences, cat.id, 'email');
      next.push(
        email ?? {
          category: cat.id,
          channel: 'email',
          enabled: false,
          email_purpose: cat.defaultPurpose,
        },
      );
      const sms = findPref(prefsQuery.data!.preferences, cat.id, 'sms');
      next.push(
        sms ?? {
          category: cat.id,
          channel: 'sms',
          enabled: false,
          email_purpose: null,
        },
      );
    });
    setDraft(next);
    setSavedSnapshot(next);
  }, [prefsQuery.data]);

  function isDirty(): boolean {
    if (draft.length !== savedSnapshot.length) return true;
    for (let i = 0; i < draft.length; i += 1) {
      const a = draft[i];
      const b = savedSnapshot[i];
      if (!a || !b) return true;
      if (
        a.enabled !== b.enabled ||
        (a.email_purpose ?? null) !== (b.email_purpose ?? null)
      ) {
        return true;
      }
    }
    return false;
  }

  function setRow(
    category: NotificationCategory,
    channel: NotificationChannel,
    patch: Partial<NotificationPreference>,
  ) {
    setDraft((prev) =>
      prev.map((p) =>
        p.category === category && p.channel === channel
          ? { ...p, ...patch }
          : p,
      ),
    );
  }

  async function handleSave() {
    setSavedFlash(false);
    await update.mutateAsync(
      { preferences: draft },
      {
        onSuccess: () => {
          setSavedSnapshot(draft);
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2500);
        },
      },
    );
  }

  if (prefsQuery.isLoading) {
    return (
      <Card title="Communication Preferences">
        <div className={styles.loading}>
          <Spinner />
        </div>
      </Card>
    );
  }

  const noEmailConfigured = activeEmailPurposes.size === 0;

  return (
    <Card title="Communication Preferences">
      <p className={styles.description}>
        Choose which automated messages this company sends. Each row also
        controls which configured email account is used for that category.
      </p>

      <div className={styles.gridWrap}>
        <table className={styles.grid} aria-label="Communication preferences">
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Email</th>
              <th scope="col">SMS</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((cat) => {
              const emailPref = findPref(draft, cat.id, 'email');
              const smsPref = findPref(draft, cat.id, 'sms');
              return (
                <tr key={cat.id}>
                  <th scope="row" aria-label={cat.label}>
                    <div className={styles.categoryName} aria-hidden>
                      {cat.label}
                    </div>
                    <p className={styles.categoryDesc}>{cat.description}</p>
                  </th>
                  <td>
                    <label className={styles.cellLabel}>
                      <input
                        type="checkbox"
                        checked={emailPref?.enabled ?? false}
                        onChange={(e) =>
                          setRow(cat.id, 'email', {
                            enabled: e.target.checked,
                          })
                        }
                        disabled={noEmailConfigured}
                        aria-label={`${cat.label} email enabled`}
                      />
                      <span>Send</span>
                    </label>
                    {emailPref?.enabled ? (
                      <label className={styles.purposeRow}>
                        <span>Send from:</span>
                        <select
                          value={emailPref.email_purpose ?? cat.defaultPurpose}
                          onChange={(e) =>
                            setRow(cat.id, 'email', {
                              email_purpose: e.target.value as OutboundPurpose,
                            })
                          }
                          aria-label={`${cat.label} email purpose`}
                        >
                          {OUTBOUND_PURPOSES.map((p) => (
                            <option
                              key={p}
                              value={p}
                              disabled={!activeEmailPurposes.has(p)}
                            >
                              {PURPOSE_LABEL[p]}
                              {!activeEmailPurposes.has(p)
                                ? ' (not configured)'
                                : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {noEmailConfigured ? (
                      <p className={styles.warning}>
                        No email account configured — set up in Email
                        Accounts above.
                      </p>
                    ) : null}
                  </td>
                  <td>
                    <label className={styles.cellLabel}>
                      <input
                        type="checkbox"
                        checked={smsPref?.enabled ?? false}
                        onChange={(e) =>
                          setRow(cat.id, 'sms', {
                            enabled: e.target.checked,
                          })
                        }
                        disabled={!smsConfigured}
                        aria-label={`${cat.label} SMS enabled`}
                      />
                      <span>Send</span>
                    </label>
                    {!smsConfigured ? (
                      <p className={styles.warning}>
                        No SMS account configured.
                      </p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        {isDirty() ? (
          <span className={styles.dirty}>Unsaved changes</span>
        ) : savedFlash ? (
          <span className={styles.saved} role="status">
            Saved ✓
          </span>
        ) : (
          <span className={styles.muted}>Up to date.</span>
        )}
        <Button
          variant="primary"
          icon={Save}
          loading={update.isPending}
          disabled={!isDirty()}
          onClick={() => void handleSave()}
        >
          Save Preferences
        </Button>
      </div>
    </Card>
  );
}
